import {
  LOCAL_STORAGE_PROMO_POPUP_COUNTER,
  LOCAL_STORAGE_RAF_POPUP_COUNTER,
  CLOSE_GAME_PROMO_POPUP_LIMIT,
  CLOSE_GAME_RAF_POPUP_LIMIT,
} from '@/helpers/constants';
import { SIGN_UP_STEPS } from '@/helpers/enums';
import websocket from '@/services/websocket';

const state = {
  alertDialog: null,
  eventDayDialog: null,
  confirmDialog: null,
  messageDialog: null,
  iframeDialog: null,
  iframeAddFriendsDialog: null,
  promptDialog: null,
  alertSnackBar: null,
  notificationTooltip: null,
  verifiedEmailDialog: false,
  showPromoPackPopup: false,
  roomBuyInGameType: null,
  showVideoPopup: false,
  showKycPopup: false,
  showSwitchCurrencyPopup: false,
  showManageFriendsPopup: false,
};

const mutations = {
  setVerifiedEmailDialog: (state, payload) => {
    state.verifiedEmailDialog = payload;
  },
  setAlertDialog: (state, payload) => {
    state.alertDialog = payload;
  },
  setEventDayDialog: (state, payload) => {
    state.eventDayDialog = payload;
  },
  setConfirmDialog: (state, payload) => {
    state.confirmDialog = payload;
  },
  setMessageDialog: (state, payload) => {
    state.messageDialog = payload;
  },
  setIFrameDialog: (state, payload) => {
    state.iframeDialog = payload;
  },
  setIFrameAddFriendsDialog: (state, payload) => {
    state.iframeAddFriendsDialog = payload;
  },
  setPromptDialog: (state, payload) => {
    state.promptDialog = payload;
  },
  setAlertSnackBar: (state, payload) => {
    state.alertSnackBar = payload;
  },
  setNotificationTooltip: (state, payload) => {
    state.notificationTooltip = payload;
  },

  SET_SHOW_PROMO_PACK_POPUP(state, value) {
    state.showPromoPackPopup = value;
  },

  SET_SHOW_VIDEO_POPUP(state, value) {
    state.showVideoPopup = value;
  },

  SET_ROOM_BUYIN_POPUP_DATA(state, value) {
    state.roomBuyInGameType = value;
  },

  SET_SHOW_KYC_POPUP(state, value) {
    state.showKycPopup = value;
  },

  SET_SHOW_SWITCH_CURRENCY_POPUP(state, value) {
    state.showSwitchCurrencyPopup = value;
  },

  SET_SHOW_MANAGE_FRIENDS_POPUP(state, value) {
    state.showManageFriendsPopup = value;
  },
};

const actions = {
  /**
   * Opens alert dialog with given data: { title, text, theme, showOverlay, permanent }
   * @param store - vuex store
   * @param {Object} data
   * @param {string} data.title
   * @param {string} data.text
   * @param {string} data.theme
   * @param {boolean} data.permanent
   * @param {boolean} data.showOverlay
   */
  showAlertDialog({ commit }, data) {
    commit('setAlertDialog', data);

    if (!data.permanent) {
      setTimeout(() => {
        commit('setAlertDialog', null);
      }, 5000);
    }
  },

  hideAlertDialog({ commit }) {
    commit('setAlertDialog', null);
  },

  showVerifiedEmailDialog({ commit }) {
    commit('setVerifiedEmailDialog', true);
  },

  showEventDayDialog({ commit }) {
    commit('setEventDayDialog', true);
  },

  hideEventDayDialog({ commit }) {
    commit('setEventDayDialog', false);
  },

  hideVerifiedEmailDialog({ commit }) {
    commit('setVerifiedEmailDialog', false);
  },

  showMessageDialog({ commit }, data) {
    commit('setMessageDialog', data);

    setTimeout(() => {
      commit('setMessageDialog', null);
    }, 3000);
  },

  hideMessageDialog({ commit }) {
    commit('setMessageDialog', null);
  },

  showConfirmDialog({ commit }, data) {
    commit('setConfirmDialog', data);
  },

  hideConfirmDialog({ commit }) {
    commit('setConfirmDialog', null);
  },

  showConfirmDialogPromise({ commit }, data) {
    return new Promise((resolve) => {
      commit('setConfirmDialog', {
        ...data,
        callback: async () => {
          await data.callback?.();
          resolve(true);
        },
      });
    });
  },

  async showIFrameDialog({ commit }, data) {
    websocket.emit('playing', { isPlaying: true });
    commit('setIFrameDialog', data);
  },

  async hideIFrameDialog({ commit /*dispatch*/ } /*, skipPopups = false*/) {
    commit('setIFrameDialog', null);
    commit('setIFrameAddFriendsDialog', null);

    websocket.emit('playing', { isPlaying: false });

    // old implementation of close game promo popup
    // if (skipPopups) return;
    // dispatch('tryToShowCloseGamePopup');
  },

  showIFrameAddFriendsDialog({ commit }, data) {
    commit('setIFrameAddFriendsDialog', data);
  },

  hideIFrameAddFriendsDialog({ commit }) {
    commit('setIFrameAddFriendsDialog', null);
  },

  showPromptDialog({ commit }, data) {
    commit('setPromptDialog', data);
  },
  hidePromptDialog({ commit }) {
    commit('setPromptDialog', null);
  },

  /**
   * Displays a snackbar with a given message.
   *
   * @param {string} data.text - The text message to be displayed in the snackbar.
   * @param {string} data.type - (Currently not in use) The type of the snackbar. This can be used to style the snackbar differently based on the type of message (e.g., 'error', 'info', 'success').
   */
  showAlertSnackBar({ commit }, data) {
    commit('setAlertSnackBar', data);
  },

  showNotificationTooltip({ commit }, data) {
    commit('setNotificationTooltip', data);
  },

  hideNotificationTooltip({ commit }) {
    commit('setNotificationTooltip', null);
  },

  async tryToShowCloseGamePopup({ state, getters, dispatch }) {
    const rafCounter = parseInt(localStorage.getItem(LOCAL_STORAGE_RAF_POPUP_COUNTER) || 0, 10);
    const promoCounter = parseInt(localStorage.getItem(LOCAL_STORAGE_PROMO_POPUP_COUNTER) || 0, 10);

    const promises = [];
    let showPopup;

    if (typeof state.packages !== 'object' || !Object.keys(state.packages).length) {
      // promises.push(dispatch('getPackages'));
    }

    await Promise.all(promises);

    if (getters.hasPromoPurchased) {
      showPopup = rafCounter < CLOSE_GAME_RAF_POPUP_LIMIT;
    } else {
      showPopup = promoCounter < CLOSE_GAME_PROMO_POPUP_LIMIT;
    }

    if (!showPopup) return;

    dispatch('showEventDayDialog');
  },

  async tryToShowPromoPopup({ state, getters, commit }) {
    const popupHasBeenShown = sessionStorage.getItem('promoPackHasBeenShown');
    if (popupHasBeenShown) return;

    const signUpStep = getters.getSignUpStep;
    const isGameOpened = state.iframeDialog !== null;
    const activePack = getters.getActivePromoPack;

    if (signUpStep > SIGN_UP_STEPS.CREDENTIALS || !activePack || isGameOpened) return;

    sessionStorage.setItem('promoPackHasBeenShown', '1');
    commit('SET_SHOW_PROMO_PACK_POPUP', true);
  },

  openKycPopup({ commit }) {
    commit('SET_SHOW_KYC_POPUP', true);
  },

  closeKycPopup({ commit }) {
    commit('SET_SHOW_KYC_POPUP', false);
  },
};

const getters = {
  getConfirmDialog: (state) => state.confirmDialog,
  getMessageDialog: (state) => state.messageDialog,
  getIFrameDialog: (state) => state.iframeDialog,
  getIFrameAddFriendsDialog: (state) => state.iframeAddFriendsDialog,
  getPromptDialog: (state) => state.promptDialog,
  getAlertSnackBar: (state) => state.alertSnackBar,
  getVerifiedEmailDialog: (state) => state.verifiedEmailDialog,
  // used in watch
  getNotificationTooltip: (state) => state.notificationTooltip,
};

export default {
  state,
  mutations,
  actions,
  getters,
};
